import {
  MESSAGE_SOURCE,
  MESSAGE_VERSION,
  RECOVERY_STATUS,
  createSessionId,
  isDeviceMissingError,
  isJitsiMessageOrigin,
  isMicPermissionError,
  pickInputDevice,
} from "./recoveryPlan";
import { createMeetingLogger } from "./meetingLog";

const FOREGROUND_SETTLE_MS = 700;
const MIN_HIDDEN_MS = 500;
const INSPECT_TIMEOUT_MS = 4000;
const MAX_AUTO_ATTEMPTS = 2;
const RECONNECTED_MS = 2500;
const BANNER_DELAY_MS = 400;

const JITSI_EVENTS = [
  "videoConferenceJoined",
  "videoConferenceLeft",
  "audioMuteStatusChanged",
  "videoMuteStatusChanged",
  "audioAvailabilityChanged",
  "videoAvailabilityChanged",
  "micError",
  "cameraError",
  "suspendDetected",
  "errorOccurred",
];

function emptySnapshot(sessionId) {
  return {
    sessionId,
    status: RECOVERY_STATUS.CONNECTED,
    detail: "",
    gestureRequired: false,
    conferenceJoined: false,
    intentionalAudioMuted: false,
    intentionalVideoMuted: false,
    iframeReady: false,
  };
}

function errorName(error) {
  if (!error) return "unknown";
  if (typeof error === "string") return error;
  return error.name || error.type || (error.error && error.error.name) || "unknown";
}

/**
 * Coordinates page lifecycle signals with the Jitsi External API.
 * Track replacement inside the meeting frame is done by
 * mobile-audio-recovery.js. This controller only falls back to
 * setAudioInputDevice when that script is not running.
 */
export function createMeetingAudioRecovery(options = {}) {
  const pageTarget = options.pageTarget || window;
  const documentTarget = options.documentTarget || document;
  const isMobile = Boolean(options.isMobile);
  const jitsiDomain = options.jitsiDomain || "";
  const targetOrigin = options.targetOrigin || (jitsiDomain ? `https://${jitsiDomain}` : "*");
  const onChange = typeof options.onChange === "function" ? options.onChange : () => {};
  const onRejoin = typeof options.onRejoin === "function" ? options.onRejoin : () => {};

  const sessionId = options.sessionId || createSessionId();
  const log = options.log || createMeetingLogger(sessionId);

  let api = null;
  let started = false;
  let disposed = false;
  let snapshot = emptySnapshot(sessionId);
  let hiddenAt = null;
  let interruptionLikely = false;
  let lock = false;
  let applyingRecovery = false;
  let autoAttempts = 0;
  let generation = 0;
  let queued = null;
  let pendingInspect = null;
  let settleTimer = null;
  let inspectTimer = null;
  let bannerTimer = null;
  let reconnectedTimer = null;
  const pageUnsubscribers = [];
  const apiUnsubscribers = [];

  function publish() {
    onChange({ ...snapshot });
  }

  function setSnapshot(patch) {
    const next = { ...snapshot, ...patch };
    const changed =
      next.status !== snapshot.status ||
      next.detail !== snapshot.detail ||
      next.gestureRequired !== snapshot.gestureRequired ||
      next.conferenceJoined !== snapshot.conferenceJoined ||
      next.iframeReady !== snapshot.iframeReady;
    snapshot = next;
    if (changed) publish();
  }

  function clearTimer(kind) {
    if (kind === "settle" && settleTimer) {
      clearTimeout(settleTimer);
      settleTimer = null;
    }
    if (kind === "inspect" && inspectTimer) {
      clearTimeout(inspectTimer);
      inspectTimer = null;
    }
    if (kind === "banner" && bannerTimer) {
      clearTimeout(bannerTimer);
      bannerTimer = null;
    }
    if (kind === "reconnected" && reconnectedTimer) {
      clearTimeout(reconnectedTimer);
      reconnectedTimer = null;
    }
  }

  function setStatus(status, extra) {
    clearTimer("reconnected");
    const detail = extra && extra.detail != null ? extra.detail : snapshot.detail;
    const gestureRequired =
      extra && extra.gestureRequired != null ? extra.gestureRequired : snapshot.gestureRequired;
    const changed =
      status !== snapshot.status || detail !== snapshot.detail || gestureRequired !== snapshot.gestureRequired;
    setSnapshot({ status, detail, gestureRequired });
    if (changed) log("status", { status, detail: detail || "" });
    if (status === RECOVERY_STATUS.RECONNECTED) {
      reconnectedTimer = setTimeout(() => {
        if (disposed || snapshot.status !== RECOVERY_STATUS.RECONNECTED) return;
        setSnapshot({
          status: RECOVERY_STATUS.CONNECTED,
          detail: "",
          gestureRequired: false,
        });
      }, RECONNECTED_MS);
    }
  }

  function postToIframe(type, extra) {
    if (!api || typeof api.getIFrame !== "function") return false;
    const frame = api.getIFrame();
    const target = frame && frame.contentWindow;
    if (!target || typeof target.postMessage !== "function") return false;
    const payload = {
      source: MESSAGE_SOURCE,
      v: MESSAGE_VERSION,
      sessionId,
      type,
      ...(extra || {}),
    };
    try {
      target.postMessage(payload, targetOrigin);
      return true;
    } catch (err) {
      log("recovery-failed", { error: "postmessage" });
      return false;
    }
  }

  function bind(target, type, handler) {
    if (!target || typeof target.addEventListener !== "function") return;
    target.addEventListener(type, handler);
    pageUnsubscribers.push(() => {
      if (typeof target.removeEventListener === "function") {
        target.removeEventListener(type, handler);
      }
    });
  }

  function onVisibility() {
    if (disposed || !snapshot.conferenceJoined) return;
    if (documentTarget.visibilityState === "hidden") {
      hiddenAt = Date.now();
      log("background", { via: "visibilitychange" });
      return;
    }
    onForeground("visibilitychange");
  }

  function onPageHide() {
    if (disposed || !snapshot.conferenceJoined) return;
    hiddenAt = Date.now();
    log("background", { via: "pagehide" });
  }

  function onPageShow() {
    onForeground("pageshow");
  }

  function onFocus() {
    onForeground("focus");
  }

  function onBlur() {
    if (disposed || !snapshot.conferenceJoined) return;
    log("blur", {});
  }

  function onOnline() {
    if (disposed || !snapshot.conferenceJoined) return;
    interruptionLikely = true;
    log("online", {});
    onForeground("online");
  }

  function onOffline() {
    if (disposed || !snapshot.conferenceJoined) return;
    log("offline", {});
  }

  function onForeground(via) {
    if (disposed || !snapshot.conferenceJoined) return;
    const hiddenFor = hiddenAt == null ? 0 : Date.now() - hiddenAt;
    const wasHidden = hiddenAt != null;
    hiddenAt = null;
    log("foreground", { via, hiddenMs: hiddenFor });

    if ((via === "focus" || via === "pageshow") && !wasHidden && !interruptionLikely && via !== "online") {
      return;
    }
    if (via === "visibilitychange" && !wasHidden && !interruptionLikely) return;

    const longEnough = hiddenFor >= MIN_HIDDEN_MS;
    const allowFallback = Boolean(isMobile && (longEnough || interruptionLikely || via === "online"));
    if (!snapshot.iframeReady && !allowFallback) return;
    scheduleInspect(via, allowFallback);
  }

  function scheduleInspect(reason, allowFallback) {
    clearTimer("settle");
    const delay = reason === "manual" ? 0 : FOREGROUND_SETTLE_MS;
    settleTimer = setTimeout(() => {
      settleTimer = null;
      beginInspect(reason, allowFallback);
    }, delay);
  }

  function waitForIframe() {
    return new Promise((resolve) => {
      clearTimer("inspect");
      inspectTimer = setTimeout(() => {
        inspectTimer = null;
        pendingInspect = null;
        resolve({ timedOut: true });
      }, INSPECT_TIMEOUT_MS);
      pendingInspect = (message) => {
        clearTimer("inspect");
        pendingInspect = null;
        resolve({ timedOut: false, message });
      };
    });
  }

  async function fallbackRecover(manual) {
    if (!api || typeof api.setAudioInputDevice !== "function") {
      setStatus(RECOVERY_STATUS.INTERRUPTED, {
        gestureRequired: true,
        detail: "Audio could not be restored from this page.",
      });
      log("recovery-failed", { error: "api-unavailable" });
      return;
    }

    let audioDeviceId = "";
    try {
      const devices = typeof api.getCurrentDevices === "function" ? await api.getCurrentDevices() : null;
      if (disposed) return;
      const audio = pickInputDevice(devices, "audioinput");
      audioDeviceId = audio.deviceId;
      await api.setAudioInputDevice(audio.label, audio.deviceId);
      if (disposed) return;
      autoAttempts = 0;
      interruptionLikely = false;
      log("recovery-completed", {
        unverified: true,
        preservedAudioMute: snapshot.intentionalAudioMuted,
        preservedVideoMute: snapshot.intentionalVideoMuted,
      });
      if (manual) {
        setStatus(RECOVERY_STATUS.INTERRUPTED, {
          gestureRequired: true,
          detail:
            "The microphone was refreshed. If you still cannot hear others, rejoin the meeting. This phone may also need a tap inside the meeting.",
        });
        return;
      }
      setStatus(RECOVERY_STATUS.CONNECTED, { detail: "", gestureRequired: false });
    } catch (err) {
      if (disposed) return;
      const name = errorName(err);
      if (!manual) autoAttempts += 1;
      log("recovery-failed", { error: name });
      if (isMicPermissionError(name)) {
        setStatus(RECOVERY_STATUS.MIC_UNAVAILABLE, {
          gestureRequired: true,
          detail: "Allow microphone access for this site, then tap Restore audio.",
        });
        return;
      }
      if (isDeviceMissingError(name) && audioDeviceId && typeof api.setAudioInputDevice === "function") {
        try {
          await api.setAudioInputDevice("", "");
          if (!disposed) {
            autoAttempts = 0;
            log("recovery-completed", { unverified: true, fallbackDevice: true });
            setStatus(manual ? RECOVERY_STATUS.INTERRUPTED : RECOVERY_STATUS.CONNECTED, {
              gestureRequired: manual,
              detail: manual
                ? "The previous microphone is gone. A default microphone was selected."
                : "",
            });
          }
          return;
        } catch (retryErr) {
          log("recovery-failed", { error: errorName(retryErr) });
        }
      }
      setStatus(RECOVERY_STATUS.INTERRUPTED, {
        gestureRequired: true,
        detail: "Audio could not be restored automatically.",
      });
    }
  }

  async function beginInspect(reason, allowFallback) {
    if (disposed) return;
    if (lock) {
      queued = {
        reason,
        allowFallback: Boolean(allowFallback || (queued && queued.allowFallback)),
      };
      return;
    }
    if (!snapshot.conferenceJoined) {
      setStatus(RECOVERY_STATUS.RECONNECTING, {
        detail: "The meeting connection dropped. Rejoin if it does not return.",
        gestureRequired: false,
      });
      return;
    }

    const manual = reason === "manual";
    if (!manual && autoAttempts >= MAX_AUTO_ATTEMPTS) {
      setStatus(RECOVERY_STATUS.INTERRUPTED, {
        gestureRequired: true,
        detail: "Audio is still interrupted. Tap Restore audio, or rejoin the meeting.",
      });
      log("recovery-failed", { error: "auto-attempt-limit" });
      return;
    }

    lock = true;
    applyingRecovery = true;
    const gen = ++generation;
    log("recovery-started", { reason, manual });

    clearTimer("banner");
    if (manual || snapshot.status !== RECOVERY_STATUS.CONNECTED) {
      setStatus(RECOVERY_STATUS.RESTORING, {
        detail: "Restoring audio…",
        gestureRequired: false,
      });
    } else {
      bannerTimer = setTimeout(() => {
        bannerTimer = null;
        if (!disposed && lock && snapshot.status === RECOVERY_STATUS.CONNECTED) {
          setStatus(RECOVERY_STATUS.RESTORING, {
            detail: "Restoring audio…",
            gestureRequired: false,
          });
        }
      }, BANNER_DELAY_MS);
    }

    const posted = postToIframe(manual ? "recover" : "inspect", { manual, reason });

    try {
      if (snapshot.iframeReady && posted) {
        const result = await waitForIframe();
        if (disposed || gen !== generation) return;
        if (result.timedOut) {
          if (!manual) autoAttempts += 1;
          log("recovery-failed", { error: "iframe-timeout" });
          setStatus(RECOVERY_STATUS.INTERRUPTED, {
            gestureRequired: true,
            detail: "Audio did not recover in time. Tap Restore audio, or rejoin the meeting.",
          });
        }
      } else if (manual || allowFallback) {
        await fallbackRecover(manual);
      }
    } finally {
      clearTimer("banner");
      if (gen === generation) {
        applyingRecovery = false;
        lock = false;
        const next = queued;
        queued = null;
        if (next && !disposed) beginInspect(next.reason, next.allowFallback);
      }
    }
  }

  function onJitsiEvent(name, payload) {
    if (disposed) return;
    if (name === "videoConferenceJoined") {
      snapshot = { ...snapshot, conferenceJoined: true };
      autoAttempts = 0;
      interruptionLikely = false;
      log("meeting-joined", {});
      setStatus(RECOVERY_STATUS.CONNECTED, { detail: "", gestureRequired: false });
      postToIframe("session", {});
      return;
    }
    if (name === "videoConferenceLeft") {
      log("meeting-left", {});
      clearTimer("settle");
      clearTimer("banner");
      setSnapshot({
        conferenceJoined: false,
        status: RECOVERY_STATUS.CONNECTED,
        detail: "",
        gestureRequired: false,
      });
      return;
    }
    if (name === "audioMuteStatusChanged") {
      if (applyingRecovery) return;
      const muted = Boolean(payload && payload.muted);
      setSnapshot({ intentionalAudioMuted: muted });
      log("audio-mute-changed", { muted });
      return;
    }
    if (name === "videoMuteStatusChanged") {
      if (applyingRecovery) return;
      const muted = Boolean(payload && payload.muted);
      setSnapshot({ intentionalVideoMuted: muted });
      log("video-mute-changed", { muted });
      return;
    }
    if (name === "micError") {
      const nameValue = errorName(payload);
      interruptionLikely = true;
      log("mic-error", { error: nameValue });
      if (isMicPermissionError(nameValue)) {
        setStatus(RECOVERY_STATUS.MIC_UNAVAILABLE, {
          gestureRequired: true,
          detail: "Allow microphone access for this site, then tap Restore audio.",
        });
        return;
      }
      scheduleInspect("mic-error", true);
      return;
    }
    if (name === "cameraError") {
      log("camera-error", { error: errorName(payload) });
      return;
    }
    if (name === "audioAvailabilityChanged") {
      const available = payload && typeof payload === "object" ? payload.available !== false : Boolean(payload);
      log("audio-availability", { available });
      if (!available) {
        interruptionLikely = true;
        scheduleInspect("audio-unavailable", true);
      }
      return;
    }
    if (name === "videoAvailabilityChanged") {
      const available = payload && typeof payload === "object" ? payload.available !== false : Boolean(payload);
      log("video-availability", { available });
      return;
    }
    if (name === "suspendDetected") {
      interruptionLikely = true;
      log("suspend-detected", {});
      scheduleInspect("suspend", true);
      return;
    }
    if (name === "errorOccurred") {
      log("conference-error", { error: errorName(payload) });
    }
  }

  function onMessage(event) {
    if (disposed || !event) return;
    if (!isJitsiMessageOrigin(event.origin, jitsiDomain)) return;
    const data = event.data;
    if (!data || data.source !== MESSAGE_SOURCE || data.v !== MESSAGE_VERSION) return;

    if (data.type === "ready") {
      setSnapshot({ iframeReady: true });
      log("iframe-ready", {});
      postToIframe("session", {});
      return;
    }
    if (data.type === "log") {
      log(data.event || "iframe", {
        detail: data.detail || "",
        error: data.error || "",
        status: data.status || "",
      });
      return;
    }
    if (data.type !== "status") return;

    if (data.conferenceJoined === false) {
      setSnapshot({ conferenceJoined: false });
    }
    if (data.status === RECOVERY_STATUS.CONNECTED || data.status === RECOVERY_STATUS.RECONNECTED) {
      autoAttempts = 0;
      interruptionLikely = false;
      log(data.status === RECOVERY_STATUS.RECONNECTED ? "recovery-completed" : "recovery-skipped", {});
    }
    if (data.status === RECOVERY_STATUS.MIC_UNAVAILABLE || data.status === RECOVERY_STATUS.INTERRUPTED) {
      log(data.status === RECOVERY_STATUS.MIC_UNAVAILABLE ? "recovery-failed" : "recovery-needs-gesture", {
        error: data.error || "",
        detail: data.detail || "",
      });
    }
    if (data.final !== false) {
      clearTimer("banner");
      setStatus(data.status || RECOVERY_STATUS.CONNECTED, {
        detail: data.detail || "",
        gestureRequired: Boolean(data.gestureRequired),
      });
      if (pendingInspect) pendingInspect(data);
      return;
    }
    if (data.status) {
      setStatus(data.status, {
        detail: data.detail || "",
        gestureRequired: Boolean(data.gestureRequired),
      });
    }
  }

  function attachApi(nextApi) {
    if (api && api !== nextApi) detachApi();
    api = nextApi;
    if (!api || typeof api.addListener !== "function") return;
    JITSI_EVENTS.forEach((eventName) => {
      const handler = (payload) => onJitsiEvent(eventName, payload);
      api.addListener(eventName, handler);
      apiUnsubscribers.push(() => {
        if (typeof api.removeListener === "function") api.removeListener(eventName, handler);
        else if (typeof api.off === "function") api.off(eventName, handler);
      });
    });
    postToIframe("session", {});
    log("api-attached", {});
  }

  function detachApi() {
    while (apiUnsubscribers.length) apiUnsubscribers.pop()();
    api = null;
  }

  function start() {
    if (started) return;
    disposed = false;
    started = true;
    log("session-started", { mobile: isMobile });
    bind(documentTarget, "visibilitychange", onVisibility);
    bind(pageTarget, "pagehide", onPageHide);
    bind(pageTarget, "pageshow", onPageShow);
    bind(pageTarget, "focus", onFocus);
    bind(pageTarget, "blur", onBlur);
    bind(pageTarget, "online", onOnline);
    bind(pageTarget, "offline", onOffline);
    bind(pageTarget, "message", onMessage);
    publish();
  }

  function detach() {
    disposed = true;
    generation += 1;
    clearTimer("settle");
    clearTimer("inspect");
    clearTimer("banner");
    clearTimer("reconnected");
    pendingInspect = null;
    while (pageUnsubscribers.length) pageUnsubscribers.pop()();
    detachApi();
    started = false;
  }

  function restoreAudio() {
    if (disposed) return;
    log("manual-recovery", {});
    interruptionLikely = true;
    autoAttempts = 0;
    beginInspect("manual", true);
  }

  function rejoin() {
    log("user-rejoined", {});
    onRejoin();
  }

  return {
    start,
    attach: attachApi,
    detach,
    restoreAudio,
    rejoin,
    getSnapshot: () => ({ ...snapshot }),
  };
}
